import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import * as PropTypes from 'prop-types';
import ClientSwitcher from '@lwt-helix/client-switcher';
import { Button } from '@lwt-helix/buttons';
import {
    selectionBrokerageStyle,
    footerIconStyles
} from './styles/ClientSelect.css';
import './styles/ClientSwitcherStyle.css';
import Routes from './../../constants/Routes';
import { getClaimsFromToken, getToken, getUserFromToken, storeToken } from '../../functions/AuthenticationFunctions';
import ErrorMessage from '../common/ErrorMessage';
import {
    fetchingUserClients,
    fetchUserClientsFailed,
    fetchUserClientsSuccess,
    refreshingToken,
    refreshTokenFailed,
    refreshTokenSuccess,
    refreshUserDetails
} from '../../actions/AuthActions';
import { authServiceApiFactory } from '../../auth/authServiceApiFactory';
import { useServiceApi } from '../../hooks/services';
import { getApiError } from '../../api/CommonFunctions';
import LoadingOverlay from '../LoadingOverlay/LoadingOverlay';
import { useAuth0 } from '@auth0/auth0-react';

export const ClientSelect = props => {
    const token = getToken();
    const { history } = props;
    const [inactive, setInactive] = useState(false);
    const [initialClientId, setInitialClientId] = useState(null);
    const authenticationReducer = useSelector(state => state.authenticationReducer);
    const [clientList, setClientList] = useState();

    const authServiceApi = useServiceApi(authServiceApiFactory);
    const { getAccessTokenSilently } = useAuth0();
    const dispatch = useDispatch();

    useEffect(() => {
        const claims = getClaimsFromToken(token);
        setInitialClientId(claims?.cid);
    }, [token]);

    const switchClient = newClientId => {
        dispatch(refreshingToken());
        const user = getUserFromToken();

        const storeIdToken = token => {
            storeToken(token, newClientId);
            dispatch(refreshUserDetails());
            dispatch(refreshTokenSuccess());
            history.replace(Routes.LANDING_PAGE);
        };

        const handleError = error => {
            dispatch(refreshTokenFailed(error));
            history.push(Routes.LOGIN);
        };

        if (user.isInternal) {
            getAccessTokenSilently({
                cacheMode: 'off',
                detailedResponse: true,
                authorizationParams: { 'boClientId': newClientId },
            }).then(response => {
                storeIdToken(response.id_token);
            }, handleError)
        } else {
            authServiceApi.refreshToken(newClientId).then(response => {
                if (!response.error) {
                    storeIdToken(response.tokens.token);
                } else {
                    handleError(response.error);
                }
            });
        }
    };

    const redirectAfterClientSelect = useCallback(() => {
        const redirect = sessionStorage.getItem('redirectAfterLogin');
        sessionStorage.removeItem('redirectAfterLogin');
        window.location.href = `${redirect}signin?access_token=${token}`;
    }, []);

    const getUserClients = () => {
        if (!clientList && token) {
            dispatch(fetchingUserClients());
            authServiceApi.getUserClients(token).then(response => {
                if (!response.error) {
                    setClientList(response.userClients);
                    dispatch(fetchUserClientsSuccess(response.userClients));
                } else {
                    dispatch(fetchUserClientsFailed(getApiError({ response: { data: response.error.body } })));
                    history.push(Routes.LOGIN);
                }
            });
        }
    };

    useEffect(async () => {
        if (!clientList) {
            getUserClients();
        } else {
            if (clientList.length === 0) {
                setInactive(true);
            } else if (clientList.length === 1) {
                if (sessionStorage.getItem('redirectAfterLogin')) {
                    redirectAfterClientSelect();
                } else {
                    switchClient(clientList[0].id);
                }
            }
        }
    }, [clientList]);

    //When user submits form, refresh token with selected clientId
    const onClientSelected = newClientId => {
        if (initialClientId !== newClientId) {
            switchClient(newClientId);
        } else {
            // user clicked on the client ID they're already on
            if (sessionStorage.getItem('redirectAfterLogin')) {
                redirectAfterClientSelect();
            } else {
                history.goBack();
            }
        }
    };

    const returnMessage = () => {
        return initialClientId ? 'Back to previous page.' : 'Back to login screen.';
    };

    const errorMessage = authenticationReducer.error
        ? `${authenticationReducer.error.code ?? 500}:${authenticationReducer.error.message ?? 'Unknown Error'}`
        : null;

    return inactive
        ? <Redirect to={Routes.INACTIVE_USER_ERROR} />
        : clientList?.length === 1
            ? <Redirect to={Routes.LANDING_PAGE} />
            : clientList?.length > 1
                ? (
                <div id="content-wrapper">
                    <div className="container">
                        <div className="vertical-center-form mb-3">
                            <div className="card mt-4" style={selectionBrokerageStyle}>
                                <div className="card-body">
                                    <h4 className="card-title mt-2">Choose a Brokerage</h4>
                                    <div className="card-block pt-3 p-md-3">
                                        <div className="row">
                                            <div className="col-12" id="clients">
                                                <ClientSwitcher
                                                    clients={Array.isArray(clientList)
                                                        ? clientList
                                                        : []
                                                    }
                                                    setClient={clientId => onClientSelected(clientId)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div className="card-footer py-4">
                                    <ErrorMessage isVisible={authenticationReducer.error} message={errorMessage} />
                                    <Button
                                        dataLwtId="redirect-to-insights"
                                        color="link"
                                        className="d-block mt-2"
                                        onClick={() => {
                                            if (initialClientId) {
                                                if (sessionStorage.getItem('redirectAfterLogin')) {
                                                    redirectAfterClientSelect()
                                                } else {
                                                    history.goBack();
                                                }
                                            } else {
                                                history.push(Routes.LOGOUT);
                                            }
                                        }}
                                        style={{ textDecoration: 'none', cursor: 'pointer', fontSize: '16px' }}
                                    >
                                        <i className="material-icons" style={{ color: '#777777' }}>arrow_back</i>
                                        <text style={footerIconStyles}>{returnMessage()}</text>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : <LoadingOverlay loading={true} />;

};

ClientSelect.propTypes = {
    history: PropTypes.object,
    onSuccess: PropTypes.func
};

export default withRouter(ClientSelect);